import React, { useState } from "react";
import { Download } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { useActiveDeal } from "../../../lib/deals/useActiveDeal";
import DealGuard from "../../../components/common/DealGuard";
import { Scale } from "../../../lib/pl/format";
import { Toggle } from "../../../components/ui/toggle";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "../../../components/ui/select";
import { usePLData } from "../../../hooks/usePLData";
import PLHeaderKPIs from "../../../components/pl/PLHeaderKPIs";
import { PLDataTable } from "../../../components/pl/PLDataTable";
import PLLegend from "../../../components/pl/PLLegend";
import KpiYearTable from "../../../components/pl/KpiYearTable";
import { toast } from "sonner";
import { useExcelExport } from "@/hooks/useExcelExport";

export default function PLStatement() {
  const { dealId, deal, status } = useActiveDeal();
  console.log("deal", deal);
  const [showRatios, setShowRatios] = useState<boolean>(true);
  const [scale, setScale] = useState<Scale>("full");
  const [selectedKpiYear, setSelectedKpiYear] = useState<number>(1);

  const { allRows, kpiRows, operatingRows, exitYear } = usePLData(deal);
  const { exportDealToExcel, isExporting } = useExcelExport();

  if (status !== "ready") return <DealGuard status={status} />;

  if (!allRows?.length) {
    return <DealGuard status="loading" />;
  }

  const yearsCount = allRows[0].years.length;

  const handleExport = async () => {
    try {
      await exportDealToExcel(deal);
      toast.success("Excel exported successfully");
    } catch (err) {
      console.error("Export failed:", err);
      toast.error("Export failed. Please try again.");
    }
  };

  return (
    <div className="w-full max-w-[95vw] mx-auto space-y-6">
      <div className="w-full rounded-xl border border-slate-200 bg-white p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              P&L Statement
            </h1>
            <p className="text-sm text-slate-600">
              USALI-compliant profit & loss with 10-year projections
            </p>
            <div className="mt-2 rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700 inline-block">
              Calculated • Read-only
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={isExporting}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              <span>{isExporting ? "Exporting…" : "Download Excel"}</span>
            </Button>

            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">Show ratios</span>
              <Toggle
                pressed={showRatios}
                onPressedChange={setShowRatios}
                className="data-[state=on]:bg-brand-600 data-[state=on]:text-white"
              >
                {showRatios ? "On" : "Off"}
              </Toggle>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">Display</span>
              <Select value={scale} onValueChange={(v) => setScale(v as Scale)}>
                <SelectTrigger className="h-9 w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Full</SelectItem>
                  <SelectItem value="thousands">Thousands</SelectItem>
                  <SelectItem value="millions">Millions</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      <PLHeaderKPIs
        kpiRows={kpiRows}
        deal={deal}
        selectedYear={selectedKpiYear}
        onYearChange={setSelectedKpiYear}
        currency={deal.currency}
        exitYear={exitYear}
        dealId={dealId!}
      />

      {exitYear && exitYear < 10 && (
        <div className="w-full rounded-lg border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-center space-x-2">
            <span className="text-blue-800 font-medium text-sm">
              Exit in Year {exitYear}. Periods after sale are zeroed.
            </span>
          </div>
        </div>
      )}

      <PLDataTable
        rows={operatingRows}
        showRatios={showRatios}
        scale={scale}
        currency={deal.currency}
        exitYear={exitYear}
      />

      <PLLegend showRatios={showRatios} />

      <KpiYearTable
        deal={deal}
        dealId={dealId!}
        yearCount={yearsCount - 1}
        currency={deal.currency}
      />
    </div>
  );
}
